import React, { useState, useEffect, useCallback } from 'react';
import {
  FiUsers, FiMail, FiSend, FiTrash2, FiSearch, FiTag,
  FiChevronDown, FiChevronUp, FiX, FiCheck, FiEdit2, FiEye,
  FiClock, FiEyeOff, FiChevronLeft, FiChevronRight
} from 'react-icons/fi';
import {
  listSubscribers, deleteSubscriber, updateSubscriber,
  getGroupSummary, sendEmail, getEmailLog
} from '../../api/subscriberApi';
import GoogleSignInGate from '../../components/GoogleSignInGate';

/* ─── Styled email template ──────────────────────────────────── */
function buildEmailHTML({ subject, body, ctaText, ctaUrl }) {
  return `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background:#f4f4f7;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">
<table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f7;padding:40px 20px;">
<tr><td align="center">
<table width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.08);">
  <!-- Header -->
  <tr><td style="background:#111827;padding:28px 40px;">
    <h1 style="margin:0;color:#fff;font-size:18px;font-weight:700;letter-spacing:0.06em;">VECTOR LABS</h1>
    <p style="margin:4px 0 0;color:rgba(255,255,255,0.55);font-size:11px;letter-spacing:0.08em;">RESEARCH &middot; PRODUCTS &middot; INNOVATION</p>
  </td></tr>
  <!-- Body -->
  <tr><td style="padding:36px 40px;">
    <h2 style="margin:0 0 16px;color:#111827;font-size:20px;font-weight:600;">${subject}</h2>
    <div style="color:#374151;font-size:15px;line-height:1.7;">
      ${body}
    </div>
    ${ctaText && ctaUrl ? `
    <table cellpadding="0" cellspacing="0" style="margin:28px 0 0;">
      <tr><td style="background:#2563eb;border-radius:6px;padding:12px 28px;">
        <a href="${ctaUrl}" style="color:#fff;text-decoration:none;font-weight:600;font-size:15px;">${ctaText}</a>
      </td></tr>
    </table>` : ''}
  </td></tr>
  <!-- Footer -->
  <tr><td style="padding:20px 40px;border-top:1px solid #e5e7eb;text-align:center;">
    <p style="margin:0;color:#9ca3af;font-size:12px;">
      Vector Labs &middot; Building the future of research tools<br>
      <a href="https://vectorlabsnc.com" style="color:#2563eb;text-decoration:none;">vectorlabsnc.com</a>
    </p>
  </td></tr>
</table>
</td></tr>
</table>
</body>
</html>`;
}

/* ─── Group / tag presets ────────────────────────────────────── */
const GROUP_PRESETS = [
  'newsletter', 'beta-searchup', 'beta-trendscan', 'beta-mecash', 'consulting', 'security-quote'
];

/* ─── Component ──────────────────────────────────────────────── */
export default function CRMDashboard() {
  const [subscribers, setSubscribers] = useState([]);
  const [groupStats, setGroupStats] = useState([]);
  const [totalStats, setTotalStats] = useState({ total: 0, unsubscribed: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [editingSub, setEditingSub] = useState(null);

  // Email composer state
  const [showComposer, setShowComposer] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailCta, setEmailCta] = useState('');
  const [emailCtaUrl, setEmailCtaUrl] = useState('');
  const [emailGroup, setEmailGroup] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [emailMode, setEmailMode] = useState('personal');

  const [selected, setSelected] = useState(new Set());

  // Email log state
  const [showLog, setShowLog] = useState(false);
  const [emailLog, setEmailLog] = useState([]);
  const [logPage, setLogPage] = useState(1);
  const [logTotalPages, setLogTotalPages] = useState(0);
  const [logTotal, setLogTotal] = useState(0);
  const [logLoading, setLogLoading] = useState(false);
  const [revealedEmails, setRevealedEmails] = useState(new Set());
  const [expandedLogId, setExpandedLogId] = useState(null);

  const loadLog = useCallback(async (page = 1) => {
    setLogLoading(true);
    try {
      const res = await getEmailLog(page, 20);
      if (res.ok) {
        setEmailLog(res.log);
        setLogPage(res.page);
        setLogTotalPages(res.totalPages);
        setLogTotal(res.total);
      }
    } catch (err) { console.error('Log load error:', err); }
    setLogLoading(false);
  }, []);

  useEffect(() => { if (showLog) loadLog(logPage); }, [showLog, logPage, loadLog]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [subRes, groupRes] = await Promise.all([
        listSubscribers({ group: filterGroup || undefined, q: searchQ || undefined }),
        getGroupSummary()
      ]);
      if (subRes.ok) setSubscribers(subRes.subscribers);
      if (groupRes.ok) {
        setGroupStats(groupRes.groups);
        setTotalStats({ total: groupRes.total, unsubscribed: groupRes.unsubscribed });
      }
    } catch (err) { console.error('CRM load error:', err); }
    setLoading(false);
  }, [filterGroup, searchQ]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this subscriber permanently?')) return;
    await deleteSubscriber(id);
    refresh();
  };

  const handleSave = async (id, data) => {
    await updateSubscriber(id, data);
    setEditingSub(null);
    refresh();
  };

  const handleSendEmail = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) return;
    setEmailSending(true);
    try {
      const html = buildEmailHTML({
        subject: emailSubject,
        body: emailBody.replace(/\n/g, '<br>'),
        ctaText: emailCta,
        ctaUrl: emailCtaUrl,
      });
      const recipientIds = selected.size ? Array.from(selected) : undefined;
      const result = await sendEmail({
        subject: emailSubject,
        htmlBody: html,
        group: !recipientIds ? emailGroup : undefined,
        recipientIds,
        mode: emailMode,
      });
      setEmailResult(result);
      if (showLog) loadLog(1);
    } catch (err) {
      setEmailResult({ ok: false, error: err.message });
    }
    setEmailSending(false);
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === subscribers.length) setSelected(new Set());
    else setSelected(new Set(subscribers.map(s => s.id)));
  };

  return (
    <GoogleSignInGate>
      <div className="admin-dashboard crm-dashboard">
        <div className="admin-header">
          <h1><FiUsers style={{ marginRight: 10 }} />Subscriber CRM</h1>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="admin-btn" onClick={() => { setShowLog(!showLog); if (!showLog) loadLog(1); }}>
              <FiClock /> Email Log
            </button>
            <button className="admin-btn" onClick={() => setShowComposer(!showComposer)}>
              <FiSend /> Compose Email
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="crm-stats">
          <div className="crm-stat-card">
            <span className="crm-stat-number">{totalStats.total}</span>
            <span className="crm-stat-label">Total Subscribers</span>
          </div>
          <div className="crm-stat-card">
            <span className="crm-stat-number">{totalStats.total - totalStats.unsubscribed}</span>
            <span className="crm-stat-label">Active</span>
          </div>
          <div className="crm-stat-card">
            <span className="crm-stat-number">{totalStats.unsubscribed}</span>
            <span className="crm-stat-label">Unsubscribed</span>
          </div>
          <div className="crm-stat-card">
            <span className="crm-stat-number">{groupStats.length}</span>
            <span className="crm-stat-label">Groups</span>
          </div>
        </div>

        {/* Group Tags */}
        <div className="crm-groups">
          <button
            className={`crm-group-tag ${!filterGroup ? 'active' : ''}`}
            onClick={() => setFilterGroup('')}
          >All</button>
          {groupStats.map(g => (
            <button
              key={g.name}
              className={`crm-group-tag ${filterGroup === g.name ? 'active' : ''}`}
              onClick={() => setFilterGroup(filterGroup === g.name ? '' : g.name)}
            >
              <FiTag size={11} /> {g.name} <span className="crm-group-count">{g.count}</span>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="crm-search">
          <FiSearch />
          <input
            type="text"
            placeholder="Search subscribers by email or name..."
            value={searchQ}
            onChange={e => setSearchQ(e.target.value)}
          />
        </div>

        {/* Email Composer */}
        {showComposer && (
          <div className="crm-composer">
            <div className="crm-composer-header">
              <h3><FiMail /> Compose Styled Email</h3>
              <button onClick={() => { setShowComposer(false); setEmailResult(null); }}><FiX /></button>
            </div>
            {emailResult ? (
              <div className={`crm-email-result ${emailResult.ok ? 'success' : 'error'}`}>
                {emailResult.ok ? (
                  <>
                    <FiCheck size={20} />
                    <span>
                      {emailResult.preview
                        ? `Preview mode: ${emailResult.sent} recipients logged to server console. Configure SMTP to send real emails.`
                        : `Sent to ${emailResult.sent} recipients. ${emailResult.failed} failed.`}
                    </span>
                  </>
                ) : (
                  <span>Error: {emailResult.error}</span>
                )}
                <button className="admin-btn" onClick={() => setEmailResult(null)}>Compose Another</button>
              </div>
            ) : (
              <>
                <div className="crm-composer-field">
                  <label>To:</label>
                  <div className="crm-composer-to">
                    {selected.size > 0
                      ? <span>{selected.size} selected subscriber(s)</span>
                      : (
                        <select value={emailGroup} onChange={e => setEmailGroup(e.target.value)}>
                          <option value="">All subscribers</option>
                          {GROUP_PRESETS.map(g => <option key={g} value={g}>{g}</option>)}
                        </select>
                      )}
                  </div>
                </div>
                <div className="crm-composer-field">
                  <label>Subject:</label>
                  <input
                    type="text"
                    value={emailSubject}
                    onChange={e => setEmailSubject(e.target.value)}
                    placeholder="Your exciting update..."
                  />
                </div>
                <div className="crm-composer-field">
                  <label>Body:</label>
                  <textarea
                    rows={6}
                    value={emailBody}
                    onChange={e => setEmailBody(e.target.value)}
                    placeholder="Write your message here. Use {{name}} for personalization..."
                  />
                </div>
                <div className="crm-composer-row">
                  <div className="crm-composer-field" style={{ flex: 1 }}>
                    <label>CTA Button Text (optional):</label>
                    <input
                      type="text"
                      value={emailCta}
                      onChange={e => setEmailCta(e.target.value)}
                      placeholder="Learn More"
                    />
                  </div>
                  <div className="crm-composer-field" style={{ flex: 1 }}>
                    <label>CTA URL:</label>
                    <input
                      type="text"
                      value={emailCtaUrl}
                      onChange={e => setEmailCtaUrl(e.target.value)}
                      placeholder="https://vectorlabsnc.com/..."
                    />
                  </div>
                </div>
                <div className="crm-composer-actions">
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginRight: 'auto' }}>
                    <label style={{ fontSize: 13, color: '#999' }}>Mode:</label>
                    <button
                      className={`admin-btn small ${emailMode === 'personal' ? 'primary' : 'ghost'}`}
                      onClick={() => setEmailMode('personal')}
                      title="Plain text, no bulk headers — lands in Primary inbox"
                      style={{ fontSize: 12, padding: '4px 10px' }}
                    >
                      ✉ Personal
                    </button>
                    <button
                      className={`admin-btn small ${emailMode === 'newsletter' ? 'primary' : 'ghost'}`}
                      onClick={() => setEmailMode('newsletter')}
                      title="HTML with unsubscribe link & headers — lands in Promotions"
                      style={{ fontSize: 12, padding: '4px 10px' }}
                    >
                      📰 Newsletter
                    </button>
                  </div>
                  <button className="admin-btn ghost" onClick={() => setShowPreview(!showPreview)}>
                    <FiEye /> {showPreview ? 'Hide' : 'Show'} Preview
                  </button>
                  <button
                    className="admin-btn primary"
                    onClick={handleSendEmail}
                    disabled={emailSending || !emailSubject.trim() || !emailBody.trim()}
                  >
                    <FiSend /> {emailSending ? 'Sending...' : 'Send Email'}
                  </button>
                </div>
                {showPreview && (
                  <div className="crm-email-preview">
                    <iframe
                      title="Email Preview"
                      srcDoc={buildEmailHTML({
                        subject: emailSubject || 'Preview Subject',
                        body: (emailBody || 'Preview body text...').replace(/\n/g, '<br>'),
                        ctaText: emailCta,
                        ctaUrl: emailCtaUrl,
                      })}
                      style={{ width: '100%', height: 500, border: 'none', borderRadius: 8, background: '#0d0d14' }}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Email Action Log */}
        {showLog && (
          <div className="crm-composer" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 16 }}><FiClock style={{ marginRight: 8 }} />Email Action Log</h3>
              <span style={{ fontSize: 13, color: '#999' }}>{logTotal} total action{logTotal !== 1 ? 's' : ''}</span>
            </div>
            {logLoading ? (
              <div style={{ textAlign: 'center', padding: 20, opacity: 0.5 }}>Loading...</div>
            ) : emailLog.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 20, opacity: 0.5 }}>No email actions yet.</div>
            ) : (
              <>
                <div className="crm-log-list">
                  {emailLog.map(entry => (
                    <div key={entry.id} className="crm-log-entry">
                      <div className="crm-log-header" onClick={() => setExpandedLogId(expandedLogId === entry.id ? null : entry.id)} style={{ cursor: 'pointer' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <strong style={{ fontSize: 14 }}>{entry.subject}</strong>
                            <span className={`crm-tag ${entry.mode === 'personal' ? 'source' : ''}`} style={{ fontSize: 11 }}>
                              {entry.mode === 'personal' ? '\u2709 Personal' : '\ud83d\udcf0 Newsletter'}
                            </span>
                          </div>
                          <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                            {new Date(entry.timestamp).toLocaleString()} &middot;{' '}
                            <span style={{ color: entry.sent > 0 ? '#4ade80' : '#999' }}>{entry.sent} sent</span>
                            {entry.failed > 0 && <span style={{ color: '#f87171' }}> &middot; {entry.failed} failed</span>}
                            {' '}&middot; {entry.recipientCount} recipient{entry.recipientCount !== 1 ? 's' : ''}
                            {entry.group && <> &middot; group: <span className="crm-tag" style={{ fontSize: 10 }}>{entry.group}</span></>}
                          </div>
                        </div>
                        <div>{expandedLogId === entry.id ? <FiChevronUp /> : <FiChevronDown />}</div>
                      </div>
                      {expandedLogId === entry.id && (
                        <div className="crm-log-detail" style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                          <div style={{ fontSize: 13, color: '#aaa', marginBottom: 6 }}>Recipients:</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {(entry.recipients || []).map(r => {
                              const isRevealed = revealedEmails.has(`${entry.id}-${r.id}`);
                              const obfuscated = r.email.replace(/^(.{2})(.*)(@.*)$/, (_, a, b, c) => a + '*'.repeat(b.length) + c);
                              return (
                                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                                  <span style={{ color: '#ccc' }}>{r.name || '(no name)'}</span>
                                  <code style={{ background: 'rgba(255,255,255,0.05)', padding: '2px 6px', borderRadius: 4, fontSize: 12, color: '#999' }}>
                                    {isRevealed ? r.email : obfuscated}
                                  </code>
                                  <button
                                    className="crm-edit-btn"
                                    style={{ fontSize: 11, padding: '1px 6px' }}
                                    onClick={() => {
                                      setRevealedEmails(prev => {
                                        const next = new Set(prev);
                                        const key = `${entry.id}-${r.id}`;
                                        next.has(key) ? next.delete(key) : next.add(key);
                                        return next;
                                      });
                                    }}
                                  >
                                    {isRevealed ? <><FiEyeOff size={10} /> Hide</> : <><FiEye size={10} /> Show</>}
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                          {entry.errors && entry.errors.length > 0 && (
                            <div style={{ marginTop: 8, fontSize: 12, color: '#f87171' }}>
                              Errors: {entry.errors.join(', ')}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {/* Pagination */}
                {logTotalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 16 }}>
                    <button
                      className="admin-btn sm ghost"
                      disabled={logPage <= 1}
                      onClick={() => setLogPage(p => Math.max(1, p - 1))}
                    >
                      <FiChevronLeft /> Prev
                    </button>
                    <span style={{ fontSize: 13, color: '#999' }}>Page {logPage} of {logTotalPages}</span>
                    <button
                      className="admin-btn sm ghost"
                      disabled={logPage >= logTotalPages}
                      onClick={() => setLogPage(p => Math.min(logTotalPages, p + 1))}
                    >
                      Next <FiChevronRight />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Subscriber Table */}
        <div className="crm-table-wrap">
          <table className="crm-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    checked={selected.size === subscribers.length && subscribers.length > 0}
                    onChange={selectAll}
                  />
                </th>
                <th>Subscriber</th>
                <th>Groups</th>
                <th>Articles Read</th>
                <th>Joined</th>
                <th style={{ width: 80 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, opacity: 0.5 }}>Loading...</td></tr>
              ) : subscribers.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, opacity: 0.5 }}>No subscribers yet.</td></tr>
              ) : subscribers.map(sub => (
                <React.Fragment key={sub.id}>
                  <tr className={`crm-row ${sub.unsubscribed ? 'unsub' : ''}`}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selected.has(sub.id)}
                        onChange={() => toggleSelect(sub.id)}
                      />
                    </td>
                    <td className="crm-subscriber-cell">
                      {sub.picture && <img src={sub.picture} alt="" className="crm-avatar" />}
                      <div>
                        <strong>{sub.name || '(no name)'}</strong>
                        <span className="crm-email">{sub.email}</span>
                      </div>
                    </td>
                    <td>
                      <div className="crm-tag-list">
                        {sub.groups.map(g => <span key={g} className="crm-tag">{g}</span>)}
                        {sub.unsubscribed && <span className="crm-tag unsub">unsubscribed</span>}
                      </div>
                    </td>
                    <td>{sub.articlesRead?.length || 0}</td>
                    <td>{new Date(sub.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="crm-actions">
                        <button onClick={() => setExpandedId(expandedId === sub.id ? null : sub.id)} title="Details">
                          {expandedId === sub.id ? <FiChevronUp /> : <FiChevronDown />}
                        </button>
                        <button onClick={() => handleDelete(sub.id)} title="Delete"><FiTrash2 /></button>
                      </div>
                    </td>
                  </tr>
                  {expandedId === sub.id && (
                    <tr className="crm-detail-row">
                      <td colSpan={6}>
                        <div className="crm-detail">
                          <div className="crm-detail-section">
                            <h4>Groups</h4>
                            {editingSub === sub.id ? (
                              <div className="crm-edit-groups">
                                {GROUP_PRESETS.map(g => (
                                  <label key={g} className="crm-check-label">
                                    <input
                                      type="checkbox"
                                      checked={(sub._editGroups || sub.groups).includes(g)}
                                      onChange={() => {
                                        const current = sub._editGroups || [...sub.groups];
                                        const next = current.includes(g)
                                          ? current.filter(x => x !== g)
                                          : [...current, g];
                                        sub._editGroups = next;
                                        setSubscribers([...subscribers]);
                                      }}
                                    />
                                    {g}
                                  </label>
                                ))}
                                <div style={{ marginTop: 8 }}>
                                  <button className="admin-btn sm" onClick={() => {
                                    handleSave(sub.id, { groups: sub._editGroups || sub.groups });
                                    delete sub._editGroups;
                                  }}><FiCheck /> Save</button>
                                  <button className="admin-btn sm ghost" onClick={() => {
                                    delete sub._editGroups;
                                    setEditingSub(null);
                                  }}><FiX /> Cancel</button>
                                </div>
                              </div>
                            ) : (
                              <div>
                                {sub.groups.length ? sub.groups.map(g => <span key={g} className="crm-tag">{g}</span>) : <span style={{ opacity: 0.4 }}>none</span>}
                                <button className="crm-edit-btn" onClick={() => setEditingSub(sub.id)}><FiEdit2 size={12} /> Edit</button>
                              </div>
                            )}
                          </div>
                          <div className="crm-detail-section">
                            <h4>Articles Read ({sub.articlesRead?.length || 0})</h4>
                            {sub.articlesRead?.length ? (
                              <ul className="crm-articles-list">
                                {sub.articlesRead.map(a => (
                                  <li key={a.slug}>{a.title} <span className="crm-read-date">{new Date(a.readAt).toLocaleDateString()}</span></li>
                                ))}
                              </ul>
                            ) : <span style={{ opacity: 0.4 }}>No articles tracked</span>}
                          </div>
                          <div className="crm-detail-section">
                            <h4>Sources</h4>
                            {sub.sources?.length ? sub.sources.map(s => <span key={s} className="crm-tag source">{s}</span>) : <span style={{ opacity: 0.4 }}>unknown</span>}
                          </div>
                          <div className="crm-detail-section">
                            <h4>Notes</h4>
                            <textarea
                              className="crm-notes"
                              defaultValue={sub.notes || ''}
                              placeholder="Add notes about this subscriber..."
                              onBlur={e => {
                                if (e.target.value !== (sub.notes || '')) {
                                  handleSave(sub.id, { notes: e.target.value });
                                }
                              }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </GoogleSignInGate>
  );
}
